import { supabase } from '@/integrations/supabase/client';

interface ErrorDetails {
  error_type: string;
  error_message: string;
  error_stack?: string;
  severity?: 'info' | 'warning' | 'error' | 'critical';
  metadata?: Record<string, any>;
}

class ErrorTracker {
  constructor() {
    this.setupGlobalErrorHandlers();
  }

  private setupGlobalErrorHandlers() {
    // Catch unhandled errors
    window.addEventListener('error', (event) => {
      this.trackError({
        error_type: 'unhandled_error',
        error_message: event.message,
        error_stack: event.error?.stack,
        severity: 'error',
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    });

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError({
        error_type: 'unhandled_rejection',
        error_message: event.reason?.message || String(event.reason),
        error_stack: event.reason?.stack,
        severity: 'error',
        metadata: {
          reason: event.reason,
        },
      });
    });
  }

  async trackError(details: ErrorDetails) {
    try {
      const { error } = await supabase.functions.invoke('track-error', {
        body: {
          ...details,
          page_url: window.location.href,
          user_agent: navigator.userAgent,
        }
      });

      if (error) {
        console.error('Failed to track error:', error);
      }
    } catch (err) {
      console.error('Error tracking failed:', err);
    }
  }

  // Public API for custom error tracking
  logError(error: Error, severity: 'error' | 'critical' = 'error', metadata?: Record<string, any>) {
    this.trackError({
      error_type: 'logged_error',
      error_message: error.message,
      error_stack: error.stack,
      severity,
      metadata,
    });
  }

  logWarning(message: string, metadata?: Record<string, any>) {
    this.trackError({
      error_type: 'warning',
      error_message: message,
      severity: 'warning',
      metadata,
    });
  }

  logInfo(message: string, metadata?: Record<string, any>) {
    this.trackError({
      error_type: 'info',
      error_message: message,
      severity: 'info',
      metadata,
    });
  }

  logCritical(message: string, error?: Error, metadata?: Record<string, any>) {
    this.trackError({
      error_type: 'critical',
      error_message: message,
      error_stack: error?.stack,
      severity: 'critical',
      metadata,
    });
  }
}

export const errorTracker = new ErrorTracker();